import React from 'react';
import { Box, Typography, Container } from '@mui/material';
import { keyframes } from '@mui/system';
import { getTimeBasedGreeting, getMotivationalTagline } from '../../utils/greetings';
import type { UserInfo } from '../../hooks/useApi';

interface HeroSectionProps {
  user: UserInfo | null | undefined;
}

// Gradient animation
const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

// Fade in animation
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const HeroSection: React.FC<HeroSectionProps> = ({ user }) => {
  const displayName = user?.email?.split('@')[0] || 'User';
  const greeting = getTimeBasedGreeting(displayName);
  const tagline = getMotivationalTagline();

  return (
    <Box
      sx={{
        position: 'relative',
        mb: 6,
        py: 8,
        background: 'linear-gradient(-45deg, #607D8B, #003F9F, #607D8B, #023a7d)',
        backgroundSize: '400% 400%',
        animation: `${gradientAnimation} 15s ease infinite`,
        borderRadius: 3,
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(245, 168, 0, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 80% 20%, rgba(32, 76, 229, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            textAlign: 'center',
            color: 'white',
            animation: `${fadeIn} 1s ease-out`,
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              textShadow: '0 2px 4px rgba(0,0,0,0.2)',
            }}
          >
            {greeting}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 300,
              opacity: 0.9,
              fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
              maxWidth: '600px',
              mx: 'auto',
              textShadow: '0 1px 2px rgba(0,0,0,0.1)',
            }}
          >
            {tagline}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};